import { useMutation } from '@apollo/client/react';
import { Plus, Inbox, ClipboardX, ClipboardCheck } from 'lucide-react';
import { Fragment } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Button } from '@/components/ui/button';
import { useAddDeposit } from 'src/context/deposit';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { AssetContentTypeEnum, ContentTypeEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';
import { SoftwareEscrowUpdateContentTypesDocument } from 'src/graphql/actions/softwareEscrow/softwareEscrowUpdateContentTypes.mutation';
import { cn } from 'src/lib/utils';
import { useAssetsContext } from 'src/containers/Deposits';

const MarkRequiredAction = ({ contentType }: { contentType: AssetContentTypeEnum }) => {
  const {
    softwareEscrow: { id: softwareEscrowId, requiredContentTypes },
  } = useSoftwareEscrow();
  const [updateSoftwareEscrow, { loading }] = useMutation(SoftwareEscrowUpdateContentTypesDocument);

  const isMarkedAsRequired = (requiredContentTypes as string[]).includes(contentType);

  const onMarkRequired = async () => {
    const newRequiredContentTypes = isMarkedAsRequired
      ? ((requiredContentTypes as string[]).filter((type) => type !== contentType) as ContentTypeEnum[])
      : ([...requiredContentTypes, contentType] as ContentTypeEnum[]);

    await updateSoftwareEscrow({
      variables: {
        softwareEscrowId,
        requiredContentTypes: newRequiredContentTypes,
      },
    });
  };

  return (
    <Button
      variant={isMarkedAsRequired ? 'outline' : 'secondary'}
      isLoading={loading}
      disabled={loading}
      onClick={onMarkRequired}
    >
      {isMarkedAsRequired ? (
        <Fragment>
          <ClipboardX />
          Mark as not required
        </Fragment>
      ) : (
        <Fragment>
          <ClipboardCheck />
          Mark as required
        </Fragment>
      )}
    </Button>
  );
};

type Props = {
  title: string;
  description: string;
};

const EmptyAssetsCard = ({ title, description }: Props) => {
  const { contentType } = useAssetsContext();
  const {
    softwareEscrow: { id, policy, requiredContentTypes, currentCompanyRole },
  } = useSoftwareEscrow();
  const {
    ui: { selectEscrow },
  } = useAddDeposit();

  const isMarkedAsRequired = (requiredContentTypes as string[]).includes(contentType);

  const toggleModal = () => selectEscrow(id);

  const EmptyTitle = isMarkedAsRequired ? (
    'No assets added'
  ) : currentCompanyRole === EscrowPartyTypeEnum.Depositor ? (
    <div className="space-y-2">
      This asset type has been marked as <strong className="font-bold">Not required</strong> to your escrow.
      <br />
      To add this type of asset, mark it as Required and start adding assets.
    </div>
  ) : (
    <div className="space-y-2">
      This asset type has been marked as <strong className="font-bold">Not required</strong> by the depositor.
    </div>
  );

  return (
    <Card className={cn(!isMarkedAsRequired && 'bg-[#F1F5F9]')}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <EmptyBlock
          title={EmptyTitle}
          icon={!isMarkedAsRequired ? ClipboardX : Inbox}
          withCard={false}
          action={
            policy.depositAssets && (
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <MarkRequiredAction contentType={contentType} />
                {isMarkedAsRequired && (
                  <Button onClick={toggleModal}>
                    <Plus />
                    Add assets
                  </Button>
                )}
              </div>
            )
          }
          classes={{
            title: 'text-sm',
            content: cn(!isMarkedAsRequired && 'bg-[#F1F5F9]'),
            icon: cn(!isMarkedAsRequired && 'bg-[#F1F5F9]'),
          }}
        />
      </CardContent>
    </Card>
  );
};

export default EmptyAssetsCard;
